import {Glue42Core} from "@glue42/core";
import {Glue42Office} from "../../../glue4office";
import {validateAttachments, validateDate, validateNewItemOptions, validatePriority} from "./validations";
import {ChunkFileParams, CreateItemObject} from "../types";
import {
    CreateItemMethodName,
    OnCancelMethodName,
    OnSendMethodName,
    ShowItemMethodName,
    TmpFilesAppendMethodName,
} from "./methodNames";
import {attachmentsErrorMessage, convertToT42Email, convertToT42Task} from "./index";
import createItemDictionary from "../dictionaries/CreateItemDictionary";

const chunkSize: number = 800000;

// TODO add action for test purposes
// export const createItem = (agm: Glue42Core.AGM.API,
//                            item: EmailParams | TaskParams,
//                            itemType: string,
//                            options?: NewEmailOptions | NewTaskOptions,
//                            action?: number) => {
export const createItem = (agm: Glue42Core.AGM.API,
                           item: Glue42Office.Outlook.EmailParams | Glue42Office.Outlook.TaskParams,
                           itemType: string,
                           options?: Glue42Office.Outlook.NewEmailOptions | Glue42Office.Outlook.NewTaskOptions) => {
    const isT42Email: boolean = itemType === "email";
    const isT42Task: boolean = itemType === "task";
    options = options ? options : {};
    const {onCanceled} = options;

    return new Promise((resolve, reject) => {
        const validItem = validateAndParseCreateItemParams(item);
        validateNewItemOptions(options);

        const internalCookie: string = new Date().getTime().toString();
        const createItemObject: CreateItemObject = {
            ItemType: itemType,
            Cookie: internalCookie,
            OnSendMethod: OnSendMethodName,
            OnCancelMethod: OnCancelMethodName,
        };

        if (isT42Email) {
            createItemObject.t42value = convertToT42Email(validItem as Glue42Office.Outlook.EmailParams);
        } else if (isT42Task) {
            createItemObject.t42value = convertToT42Task(validItem as Glue42Office.Outlook.TaskParams);
        }

        const successHandler = () => {
            const onSent: (item: Glue42Office.Outlook.T42Email | Glue42Office.Outlook.T42Task) => any = isT42Email
                ? (options as Glue42Office.Outlook.NewEmailOptions).onSent
                : (options as Glue42Office.Outlook.NewTaskOptions).onSaved;
            createItemDictionary.add(internalCookie, {onSent, onCanceled});
            resolve();
        };

        const errorHandler = (err: any) => {
            reject(err.message);
            return;
        };

        const showItem = (r: { returned: { ItemID: object } }, itemOptions?: Glue42Office.Outlook.CommonItemOptions) => {
            const showItemObject = r.returned;
            if (itemOptions) {
                (showItemObject as any).displaySettings = options?.displaySettings;
            }
            agm.invoke(ShowItemMethodName, showItemObject)
                .then(successHandler)
                .catch(errorHandler);
        };

        const {attachments} = validItem;
        if (attachments && attachments.length > 0) {
            if (isT42Email) {
                createItemObject.t42value.attachments = [];
            } else {
                createItemObject.AttachFiles = [];
            }

            const att = attachments[0] as Glue42Office.Outlook.AttachmentParams;
            const createNewFile = typeof att.data === "string";
            if (createNewFile) {
                Promise.all((attachments as Glue42Office.Outlook.AttachmentParams[])
                    .map(async (file: Glue42Office.Outlook.AttachmentParams) => {
                        file.truncate = true;
                        file.encoding = "base64";
                        // chunk by 1 MB
                        if (new Blob([file.data]).size > 1000000) {
                            return chunkAndAppendAttachment(agm, file, file.data, isT42Email, createItemObject);
                        } else {
                            return agm.invoke(TmpFilesAppendMethodName, file)
                                .then((returnedFile: { returned: { Filename: string } }) => {
                                    const fileName: string = returnedFile.returned.Filename;
                                    if (isT42Email) {
                                        (createItemObject.t42value.attachments as Glue42Office.Outlook.T42Attachment[])
                                            .push({name: fileName});
                                    } else {
                                        (createItemObject.AttachFiles as string[]).push(fileName as string);
                                    }
                                })
                                .catch((err) => window.console.warn("Not able to create attachment file"));
                        }
                    }))
                    .then(() => {
                        agm.invoke(CreateItemMethodName, createItemObject)
                            .then((r) => showItem(r, options))
                            .catch((err) => reject(err.message));
                    })
                    .catch((err) => reject(err.message));
            } else if (typeof attachments[0] === "string") {
                // Attach existing file(s)
                (attachments as string[]).map((file: string) => {
                    return isT42Email
                        ? (createItemObject.t42value.attachments as Glue42Office.Outlook.T42Attachment[])
                            .push({name: file as string})
                        : (createItemObject.AttachFiles as string[]).push(file as string);
                });
                agm.invoke(CreateItemMethodName, createItemObject)
                    .then((r) => showItem(r, options))
                    .catch((err) => reject(err.message));
            } else {
                reject(attachmentsErrorMessage);
            }
        } else {
            agm.invoke(CreateItemMethodName, createItemObject)
                .then((r) => showItem(r, options))
                .catch((err) => reject(err.message));
        }
    });
};

const validateAndParseCreateItemParams = (params: Glue42Office.Outlook.EmailParams | Glue42Office.Outlook.TaskParams) => {
    const validParams: Glue42Office.Outlook.EmailParams | Glue42Office.Outlook.TaskParams = parseCreateItemParams(params);
    if (validParams.attachments) {
        validParams.attachments = validateAttachments(params.attachments);
    }
    if ((validParams as Glue42Office.Outlook.TaskParams).startDate) {
        validParams.startDate = validateDate((validParams as Glue42Office.Outlook.TaskParams).startDate, "startDate");
    }
    if ((validParams as Glue42Office.Outlook.TaskParams).dueDate) {
        validParams.dueDate = validateDate((validParams as Glue42Office.Outlook.TaskParams).dueDate, "dueDate");
    }
    if ((validParams as Glue42Office.Outlook.TaskParams).reminderTime) {
        validParams.reminderTime = validateDate((validParams as Glue42Office.Outlook.TaskParams).reminderTime, "reminderTime");
    }
    if ((validParams as Glue42Office.Outlook.TaskParams).priority) {
        validParams.priority = validatePriority((validParams as Glue42Office.Outlook.TaskParams).priority);
    }
    return validParams;
};

const parseCreateItemParams = (params: Glue42Office.Outlook.EmailParams | Glue42Office.Outlook.TaskParams) => {
    const newParams: Glue42Office.Outlook.EmailParams | Glue42Office.Outlook.TaskParams = Object.keys(params)
        .reduce((prev: Glue42Office.Outlook.EmailParams | Glue42Office.Outlook.TaskParams, curr: string) => {
            prev[curr] = params[curr];
            return prev;
        }, {});

    if (newParams.subject) {
        newParams.subject = newParams.subject.toString();
    }
    if (newParams.body) {
        newParams.body = newParams.body.toString();
    }
    if ((newParams as Glue42Office.Outlook.EmailParams).bodyHtml) {
        (newParams as Glue42Office.Outlook.EmailParams).bodyHtml = (newParams as Glue42Office.Outlook.EmailParams)
            .bodyHtml.toString();
    }
    if ((newParams as Glue42Office.Outlook.TaskParams).sender) {
        (newParams as Glue42Office.Outlook.TaskParams).sender.toString();
    }
    return newParams;
};

const chunkAndAppendAttachment = (agm: Glue42Core.AGM.API,
                                  file: Glue42Office.Outlook.AttachmentParams,
                                  data: string,
                                  isT42Email: boolean,
                                  createItemObject: CreateItemObject): Promise<any> => {
    return new Promise((resolve: () => void) => {
        const {chunkFile, chunkData} = updateChunkFile(file, data);
        invokeFileAppend(agm, chunkFile, chunkData, resolve, isT42Email, createItemObject);
    });
};

const invokeFileAppend = (agm: Glue42Core.AGM.API,
                          file: Glue42Office.Outlook.AttachmentParams,
                          data: string,
                          resolve: () => void,
                          isT42Email: boolean,
                          createItemObject: CreateItemObject): void => {
    agm.invoke(TmpFilesAppendMethodName, file)
        .then((returnedFile: any) => {
            if (data.length > 0) {
                file.truncate = false;
                const {chunkFile, chunkData} = updateChunkFile(file, data);
                return invokeFileAppend(agm, chunkFile, chunkData, resolve, isT42Email, createItemObject);
            } else {
                const fileName: string = returnedFile.returned.Filename;
                if (isT42Email) {
                    (createItemObject.t42value.attachments as Glue42Office.Outlook.T42Attachment[])
                        .push({name: fileName});
                } else {
                    (createItemObject.AttachFiles as string[]).push(fileName as string);
                }
                resolve();
            }
        })
        .catch(() => {
            window.console.warn("Not able to create attachment file");
        });
};

const updateChunkFile = (file: Glue42Office.Outlook.AttachmentParams, data: string): ChunkFileParams => {
    file.data = data.slice(0, chunkSize);
    data = data.slice(chunkSize, data.length);
    return {chunkFile: file, chunkData: data};
};
